import React, { useMemo, useRef, useState } from "react";
import { FiDownload } from "react-icons/fi";

import { ReactComponent as ErodeIcon } from "../../assets/icons/erode_icon.svg";
import { ReactComponent as DotsVerticalIcon } from "../../assets/icons/dots-vertical.svg";
import ProfilePictureRounded from "../ProfilePictureRounded";
import PostSampleButton from "./PostSampleButton";
import { Skeleton, SkeletonText } from "../Skeleton";
import { useDismissOnOutsideOrEscape } from "../../hooks/useOutsideClick";
import { POLLER_INTERVAL_LABEL } from "../../utils/processedModelScheduleSync";

import {
  describeLastRun,
  describeSchedule,
  getBandColor,
  getModelCategory,
  getTemplateName,
  LatestRunState,
  ModelSchemaLike,
  pickSubject,
  RUN_LOAD_FAILED,
  TemplatesById,
} from "../../utils/modelsList";

// undefined = the owner lookup is still in flight, null = no owner or lookup failed
export interface ModelSchemaOwner {
  name?: string;
  profilePictureUrl?: string;
}

interface ModelSchemaCardProps {
  model: ModelSchemaLike;
  templatesById: TemplatesById;
  owner: ModelSchemaOwner | null | undefined;
  latestRun: LatestRunState;
  isToggling: boolean;
  isQueuing: boolean;
  hasQueuedRun: boolean;
  // Mutations are owner-or-Admin only; org-mates and AdminReadOnly get a read-only card
  canManage: boolean;
  onToggleActive: (model: ModelSchemaLike) => void;
  onRunOnce: (model: ModelSchemaLike) => void;
  onEdit: (model: ModelSchemaLike) => void;
  onExport: (model: ModelSchemaLike) => void;
  // Optional: when set, the whole card opens the model's outputs, like DatasetCard
  onOpen?: (model: ModelSchemaLike) => void;
  /** HISTORICAL models (stage 4): the status line replaces the schedule and last-run line, Run once becomes Run now
   * (works while paused), and the switch means paused (uploads don't start runs) */
  statusLine?: string;
  /** External forecast models (template.external): Run once becomes Upload forecast, since nothing runs */
  onUploadForecast?: (model: ModelSchemaLike) => void;
  /** Where the Admin-only sample POST button reports a failure (the card has no room for the message) */
  onPostSampleError?: (message: string) => void;
}

const ModelSchemaCard: React.FC<ModelSchemaCardProps> = ({
  model,
  templatesById,
  owner,
  latestRun,
  isToggling,
  isQueuing,
  hasQueuedRun,
  canManage,
  onToggleActive,
  onRunOnce,
  onEdit,
  onExport,
  onOpen,
  statusLine,
  onUploadForecast,
  onPostSampleError,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useDismissOnOutsideOrEscape(menuOpen, menuRef, () => setMenuOpen(false));

  const isActive = !!model.isActive;
  const lastRun = describeLastRun(latestRun);
  const templateName = getTemplateName(model, templatesById);
  // Owner may be unresolvable (deleted user, or a schema created before owner was stored)
  const ownerName = owner?.name || "Unknown user";
  // The glyph and band name the model's subject (wind, solar, hydro…); the Erode mark on its hashed band stands in when nothing matches
  const category = getModelCategory(model, templatesById);
  // The name leads: a template category is broad ("Energy" would outvote "Wind …"), so it only decides when the name says nothing
  const subject = useMemo(
    () => pickSubject(model.name, category, model.schemaType, model.description),
    [model.name, category, model.schemaType, model.description]
  );
  const SubjectIcon = subject?.Icon;
  const isHistorical = model.modelKind === "HISTORICAL";
  // An external template has no processor: its forecasts are uploaded, so the model has no schedule and never runs
  const isExternal = templatesById?.[model.schemaType || ""]?.external === true;
  // A real run only shows on an external card if one somehow exists (there should be none); loading/none/failed is left out
  const externalLastRun = latestRun && latestRun !== RUN_LOAD_FAILED ? lastRun.label : null;
  const runLabel = hasQueuedRun ? "Queued" : isQueuing ? "Queuing…" : isHistorical ? "Run now" : "Run once";
  // Held until the poller's tick elapses; triggerRunOnce also dedupes against a pending row so neither a reload nor the button re-enabling can double-queue.
  // A historical model's Run now is an explicit ask, so it works while the model is paused (stage 4, decision 8)
  const runDisabled = (!isActive && !isHistorical) || isQueuing || hasQueuedRun;

  const activate = onOpen ? () => onOpen(model) : undefined;
  // Inner controls stop propagation so managing the model never also opens its outputs
  const stopOpen = activate ? (e: React.SyntheticEvent) => e.stopPropagation() : undefined;

  // Mirrors the website's mega-menu card: band rail left, copy right
  return (
    <div
      className={`eai-rail-card group w-full min-w-0 ${activate ? "cursor-pointer" : ""}`}
      role={activate ? "button" : undefined}
      tabIndex={activate ? 0 : undefined}
      aria-label={activate ? `View outputs for ${model.name}` : undefined}
      onClick={activate}
      onKeyDown={
        activate
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                activate();
              }
            }
          : undefined
      }
    >
      <div
        className={`eai-rail ${subject?.band ?? getBandColor(model.id)}`}
      >
        {SubjectIcon ? (
          <SubjectIcon aria-hidden className="h-9 w-9 text-white" />
        ) : (
          <div className="w-9 h-9" style={{ filter: "brightness(0) invert(1)" }}>
            <ErodeIcon className="w-full h-full" />
          </div>
        )}
      </div>

      {/* Shared gap; only the padding is tighter: this card carries two more rows than a group tile and still has to land on eai-rail-card's 208px floor */}
      {/* Every block sits one gap-3 apart, like DatasetCard; no reserved heights, so the spacing is the same top to bottom */}
      <div className="eai-rail-copy gap-3 py-3">
        <div className="flex items-start gap-2.5">
          <h3 className="eai-card-title line-clamp-2 min-w-0 flex-1 break-words" title={model.name}>
            {model.name}
          </h3>

          {/* -my-0.5 lets the taller read-only pill overhang, so the row is only as tall as the title */}
          <div className="shrink-0 -my-0.5 flex items-center gap-2" onClick={stopOpen} onKeyDown={stopOpen}>
            {/* A static pill reads clearer than a disabled switch the viewer can never flip */}
            {canManage ? (
              <button
                type="button"
                role="switch"
                aria-checked={isActive}
                aria-label={isActive ? "Deactivate model" : "Activate model"}
                disabled={isToggling}
                title={
                  isHistorical
                    ? isActive
                      ? "Active — runs when its dataset gets new data. Click to pause."
                      : "Paused — uploads don't start runs. Click to activate."
                    : isActive
                      ? "Active — runs on its schedule. Click to deactivate."
                      : "Inactive — scheduled runs are stopped. Click to activate."
                }
                onClick={() => onToggleActive(model)}
                className={`relative inline-flex items-center h-4 w-8 p-0.5 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isActive ? "bg-eai-cta-primary" : "bg-eai-border-hover"
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 rounded-full bg-eai-surface-card shadow-eai-subtle transition-transform ${
                    isActive ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            ) : (
              <span
                title="You have read-only access to this model"
                className={`eai-tag-pill-sm ${
                  isActive
                    ? "border-eai-success-border bg-eai-success-surface text-eai-success-text"
                    : "border-eai-border bg-eai-surface-hover text-eai-ink-muted"
                }`}
              >
                {isActive ? "Active" : isHistorical ? "Paused" : "Inactive"}
              </span>
            )}
          </div>
        </div>

        {model.description && (
          <p className="eai-card-description min-h-0" title={model.description}>
            {model.description}
          </p>
        )}

        {/* Hairline splits what the model is from what it is doing; pt-3 matches the gap-3 above it */}
        <div className="flex flex-col gap-3 border-t border-eai-border pt-3">
          {/* A historical model's line is its data and its last run ("Data to 14 Oct 22:00 · last run succeeded 2 h ago"), never a schedule */}
          {statusLine !== undefined ? (
            <div className="flex flex-wrap items-center" title={statusLine}>
              <span className="break-words font-inter text-eai-body-xs font-medium text-eai-ink" data-testid="historical-status-line">
                {statusLine}
              </span>
            </div>
          ) : isExternal ? (
            /* An external model's line says where its forecasts come from; there is no schedule to describe */
            <div className="flex flex-wrap items-center gap-x-1.5" title={`Forecasts are uploaded · no schedule${externalLastRun ? ` · ${externalLastRun}` : ""}`}>
              <span className="break-words font-inter text-eai-body-xs font-medium text-eai-ink" data-testid="external-status-line">
                Forecasts are uploaded · no schedule
                {externalLastRun && " ·"}
              </span>
              {externalLastRun && (
                <span className={`whitespace-nowrap font-inter text-eai-body-xs ${lastRun.tone}`}>{externalLastRun}</span>
              )}
            </div>
          ) : (
          <>
          {/* Schedule and last run, unlabelled since each value says what it is, share a line, and wrap rather than clip on a narrow card. undefined = the per-card run query is still in flight, so hold a placeholder not a dash */}
          <div className="flex flex-wrap items-center gap-x-1.5" title={`${describeSchedule(model.runSchedule)}${latestRun === undefined ? "" : ` · ${lastRun.label}`}`}>
            <span className="break-words font-inter text-eai-body-xs font-medium text-eai-ink">
              {/* The separator trails the schedule, so a wrapped last run starts its line clean */}
              {describeSchedule(model.runSchedule)}
              {latestRun !== undefined && " ·"}
            </span>
            {latestRun === undefined ? (
              <Skeleton className="h-2.5 w-20" />
            ) : (
              <span className={`whitespace-nowrap font-inter text-eai-body-xs ${lastRun.tone}`}>{lastRun.label}</span>
            )}
          </div>
          </>
          )}
          {/* The version is a fact, not a state, so it takes the same neutral pill as the dataset and template cards */}
          {(model.version || isHistorical || isExternal) && (
            <div className="flex flex-wrap gap-1">
              {isHistorical && (
                <span className="eai-tag-pill-sm whitespace-nowrap border-eai-teal-deep/40 text-eai-teal-deep" data-testid="historical-pill">
                  HISTORICAL
                </span>
              )}
              {isExternal && (
                <span className="eai-tag-pill-sm whitespace-nowrap border-eai-teal-deep/40 text-eai-teal-deep" data-testid="external-pill">
                  EXTERNAL
                </span>
              )}
              {model.version && <span className="eai-tag-pill-sm whitespace-nowrap">v{model.version}</span>}
            </div>
          )}
        </div>

        {/* Run once leads on the left; edit and the utilities group on the right. relative: the ⋯ menu anchors to this row, not its trigger */}
        <div
          className="relative mt-auto flex items-center justify-between gap-2 pt-2.5 border-t border-eai-border"
          onClick={stopOpen}
          onKeyDown={stopOpen}
        >
          {canManage && isExternal ? (
            /* Nothing to run: the owner sends the model's next forecast as a file instead */
            <button
              type="button"
              onClick={() => onUploadForecast?.(model)}
              title="Upload a forecast file for this model"
              className="eai-btn-cta-xs"
              data-testid="upload-forecast"
            >
              Upload forecast
            </button>
          ) : canManage ? (
            <button
              type="button"
              onClick={() => onRunOnce(model)}
              disabled={runDisabled}
              title={
                isHistorical
                  ? hasQueuedRun
                    ? "Queued · starts within seconds"
                    : "Process from the last successful point to now"
                  : !isActive
                  ? "Activate the model to queue a run"
                  : hasQueuedRun
                  ? `A run is already queued — it starts within ${POLLER_INTERVAL_LABEL}`
                  : `Queue a one-off run (starts within ${POLLER_INTERVAL_LABEL})`
              }
              className="eai-btn-cta-xs"
            >
              {runLabel}
            </button>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-1.5">
            {/* Leads the group and shows on hover (always on touch); held open while its menu is. It carries the model's provenance — template and owner */}
            <div ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`More options for ${model.name}`}
                title="More options"
                data-testid="model-card-menu"
                className={`eai-btn-icon ${menuOpen ? "border-eai-border-active bg-eai-surface-hover" : "eai-card-reveal"}`}
              >
                <DotsVerticalIcon className="h-3.5 w-3.5 [&_path]:stroke-current" />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  /* Dropped UP and right-aligned to the action row, so it stays over the copy column and never covers the rail */
                  className="eai-card absolute bottom-full right-0 z-30 mb-1.5 w-56 max-w-full p-1"
                >
                  <div className="px-2 py-1.5">
                    <div className="eai-meta-label">Template</div>
                    {/* Clamped: eai-rail-card clips its overflow, so an unbounded menu could grow past the card's 208px and lose its own last row. */}
                    <div
                      className="mt-0.5 line-clamp-2 font-inter text-eai-body-xs font-medium text-eai-ink"
                      title={templateName}
                    >
                      {templateName}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <ProfilePictureRounded
                      pictureURL={owner?.profilePictureUrl}
                      diameter={22}
                      alt={ownerName}
                      className="shrink-0"
                      onClick={undefined}
                    />
                    {/* undefined = the owner lookup is still in flight, so hold a placeholder not a dash */}
                    {owner === undefined ? (
                      <Skeleton className="h-2.5 w-24" />
                    ) : (
                      <span
                        className="min-w-0 truncate font-inter text-eai-body-xs font-medium text-eai-ink-muted"
                        title={ownerName}
                      >
                        {ownerName}
                      </span>
                    )}
                  </div>

                  {/* Only under sm — above it the row's own icon button is visible */}
                  <div className="sm:hidden">
                    <div className="my-1 h-px bg-eai-border" />
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        onExport(model);
                      }}
                      className="eai-menu-item flex w-full items-center gap-2 text-eai-body-xs"
                    >
                      <FiDownload size={12} className="shrink-0" />
                      {/* Short label: the menu is capped to the card's copy column, which wrapped "Export schema JSON" */}
                      Export JSON
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Admins only (the component checks): the body this model's run POSTs to its executionUrl. Hidden under sm with Export */}
            <PostSampleButton modelId={model.id} modelName={model.name} onError={onPostSampleError} className="hidden sm:inline-flex" />
            {/* Export stays ungated — it is a read, and the viewer can already read this schema. Hidden under sm, where it would break the row: the ⋯ menu carries it there. */}
            <button
              type="button"
              onClick={() => onExport(model)}
              aria-label={`Export ${model.name} schema JSON`}
              title="Export schema JSON"
              className="eai-btn-icon hidden sm:inline-flex"
            >
              <FiDownload size={12} />
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => onEdit(model)}
                className="eai-btn-outline-xs"
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder matching ModelSchemaCard's layout — keep the two in sync when the card changes.
export const ModelSchemaCardSkeleton: React.FC = () => (
  <div className="eai-rail-card w-full min-w-0">
    <Skeleton className="eai-rail" radius="none" />
    <div className="eai-rail-copy gap-3 py-3">
      {/* Mirrors the real card's blocks, so nothing shifts on load */}
      <div className="flex items-start gap-2.5">
        <div className="flex-1 min-w-0 flex items-start gap-1.5">
          <Skeleton className="h-3.5 w-2/3" />
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <Skeleton className="h-4 w-8" radius="full" />
        </div>
      </div>

      <SkeletonText lines={2} lineClassName="h-3" />

      <div className="flex flex-col gap-3 border-t border-eai-border pt-3">
        <div className="flex h-4 items-center">
          <Skeleton className="h-3 w-28" />
        </div>
        <Skeleton className="h-[23px] w-12" radius="full" />
      </div>

      <div className="mt-auto flex items-center justify-between gap-2 pt-2.5 border-t border-eai-border">
        <Skeleton className="h-eai-control-icon w-16" radius="full" />
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-eai-control-icon w-eai-control-icon" radius="full" />
          <Skeleton className="hidden h-eai-control-icon w-eai-control-icon sm:block" radius="full" />
          <Skeleton className="h-eai-control-icon w-10" radius="full" />
        </div>
      </div>
    </div>
  </div>
);

export default ModelSchemaCard;
